import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
} from "../components/ui/dialog";
import { Crown, CheckCircle } from "@phosphor-icons/react";
import { useAuth, apiClient } from "../App";
import { useState } from "react";
import { toast } from "sonner";
import { canPurchase, getUnavailableCopy } from "../lib/billingProvider";

const UpgradeModal = ({ isOpen, onClose, reason }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const purchasable = canPurchase();
  const nativeMsg = getUnavailableCopy("subscription");

  const getMessage = () => {
    if (reason === "matches") return "You've reached your daily matches on the free plan.";
    if (reason === "messages") return "Upgrade to keep the conversation going freely.";
    if (reason === "presence") return "Fuller Presence lets you view and share more of who you are.";
    return "Go deeper in your connections with ETHOS Plus.";
  };

  const handleUpgrade = async () => {
    if (!purchasable) return;
    setLoading(true);
    try {
      const res = await apiClient.post("/subscription/checkout", { origin_url: window.location.origin });
      window.location.href = res.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start checkout. Please try again.");
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className="bg-[#FAFAF7] border-[#E6E2DA] rounded-3xl max-w-sm mx-4 p-0 overflow-hidden"
        data-testid="upgrade-modal"
      >
        {/* Header */}
        <div className="bg-gradient-to-br from-[#C9A24D] to-[#9A7035] px-6 pt-6 pb-5 text-center">
          <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
            <Crown size={28} weight="fill" className="text-white" />
          </div>
          <h2 className="font-heading text-xl font-bold text-white">ETHOS Plus</h2>
          <p className="text-white/85 text-sm mt-1">Go deeper, not louder.</p>
        </div>

        {/* Body */}
        <div className="px-6 pt-5 pb-6" data-light-sheet>
          <p className="text-center text-sm text-[#403833] mb-5 leading-relaxed">{getMessage()}</p>

          <div className="space-y-2.5 mb-5">
            {[
              "Fuller Presence — larger photos and expanded profiles",
              "More daily discovery and Direct Connect requests",
              "Private Offerings and ETHOS Eden access",
              "Expanded values-aligned reach — alignment-first discovery",
            ].map(feat => (
              <div key={feat} className="flex items-start gap-2.5">
                <CheckCircle size={16} weight="fill" className="text-[#C9A24D] mt-0.5 shrink-0" />
                <span className="text-sm text-[#403833]">{feat}</span>
              </div>
            ))}
          </div>

          <div className="text-center mb-1.5">
            <span className="text-2xl font-heading font-bold text-[#403833]">A$9.99</span>
            <span className="text-[#78716C] text-sm">/month</span>
          </div>
          <p className="text-[11px] text-center text-[#9A928A] mb-5">
            Monthly subscription · Cancel anytime in Settings
          </p>

          {!purchasable ? (
            <div className="rounded-2xl bg-[#F6F4EF] border border-[#E6E2DA] p-3 mb-4 text-sm text-[#78716C]">
              {nativeMsg}
            </div>
          ) : (
            <button
              onClick={handleUpgrade}
              disabled={loading}
              className="w-full h-12 rounded-full text-white font-semibold text-sm mb-3"
              style={{ background: "linear-gradient(135deg,#C9A24D,#9A7035)" }}
              data-testid="upgrade-modal-btn"
            >
              {loading ? "Loading…" : "Upgrade to ETHOS Plus"}
            </button>
          )}

          <button
            onClick={() => { onClose(); navigate("/store"); }}
            className="w-full text-center text-sm text-[#6B4E8A] hover:text-[#403833] transition-colors mb-1"
            data-testid="upgrade-modal-view-plans"
          >
            View upgrade options →
          </button>
          <button
            onClick={onClose}
            className="w-full text-center text-sm text-[#9A928A] hover:text-[#78716C] transition-colors"
            data-testid="upgrade-modal-close"
          >
            Keep connecting values-first
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default UpgradeModal;
